import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  composeSetName,
  type ImageSet,
  type ImageSetItem,
  type Product,
  type Verdict,
} from '@visionqc/shared';
import { api } from '../api/client';
import { useProducts } from '../api/useProducts';
import { Combobox } from '../components/Combobox';
import type { Customer } from '@visionqc/shared';
import { useImageUrl } from '../api/useImageUrl';
import { VerdictBadge } from '../components/common';

interface UploadItem {
  file: File;
  preview: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  error?: string;
}

const CONCURRENCY = 3;

/**
 * Dataset page — the dataset is the top-level thing.
 *
 * Work arrives as a batch from a customer on a date, and a batch may contain
 * several products; which product each image is only becomes clear once you're
 * looking at them. So you create the dataset first (customer · label · date),
 * then choose a product per upload inside it. One dataset can therefore hold
 * images of several products, and the gallery shows which is which.
 */
/** Verdict styling, so a label never shows in a colour that contradicts it. */
const LABEL_BADGE: Record<string, string> = { GOOD: 'good', BAD: 'bad', UNSURE: 'unsure' };
const LABEL_ICON: Record<string, string> = { GOOD: '\u2713', BAD: '\u25a0', UNSURE: '?' };

/**
 * Create a product without leaving the upload.
 *
 * Which product an image is only becomes clear while you are looking at it, so
 * being sent to Settings to invent a SKU loses your place in the batch. A
 * product is a KIND of thing, not one picture: it carries the defect
 * vocabulary, the threshold and the model, which is exactly what every image
 * of that kind should share.
 */
function NewProductInline({
  customerId,
  customerName,
  onCreated,
  onCancel,
  onError,
}: {
  /** Whose product this becomes. Not optional — see CreateProductSchema. */
  customerId: number;
  customerName: string | null;
  onCreated: (p: Product) => void | Promise<void>;
  onCancel: () => void;
  onError: (m: string) => void;
}) {
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [applyLibrary, setApplyLibrary] = useState(true);
  const [busy, setBusy] = useState(false);

  // Derive the SKU so it is one less thing to invent; still editable.
  const suggestedSku =
    name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 24) || '';

  const create = async () => {
    setBusy(true);
    try {
      const product = await api.createProduct({
        customerId,
        name: name.trim(),
        sku: (sku || suggestedSku).trim(),
        active: true,
        // A product created from an uploaded image set is a quality inspection:
        // you have pictures of a thing and want to know which are acceptable.
        // Classification starts from a list of classes instead, and is chosen
        // deliberately in Settings.
        task: 'anomaly',
        threshold: 0.5,
        uncertaintyBand: 0,
      });
      // A product with no defect vocabulary gives operators an empty list the
      // moment they start annotating, which is the cold start we keep hitting.
      if (applyLibrary) {
        const library = await api.listDefectLibrary().catch(() => []);
        for (const dt of library) await api.applyDefectType(product.id, dt.id);
      }
      await onCreated(product);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="panel"
      style={{ marginTop: 12, background: 'var(--panel-2)', borderLeft: '3px solid var(--accent)' }}
    >
      <strong style={{ fontSize: 14 }}>New product</strong>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 10px' }}>
        A product is a kind of part, not one picture — every image you upload as
        this product shares its defect list and threshold.
        {customerName && <> It will be uploaded under <strong>{customerName}</strong>.</>}
      </p>
      <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <label>Product name</label>
          <input
            autoFocus
            value={name}
            placeholder="e.g. Chocolate chip cookie"
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && name.trim() && void create()}
            style={{ width: '100%' }}
          />
        </div>
        <div>
          <label>SKU</label>
          <input
            value={sku || suggestedSku}
            onChange={(e) => setSku(e.target.value)}
            style={{ width: 160 }}
          />
        </div>
        <button className="btn primary" disabled={!name.trim() || busy} onClick={() => void create()}>
          {busy ? 'Creating…' : 'Create & use'}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <label className="row" style={{ gap: 8, marginTop: 10, fontSize: 13, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={applyLibrary}
          onChange={(e) => setApplyLibrary(e.target.checked)}
        />
        Start with the whole defect library — untick the ones that can't happen later
      </label>
    </div>
  );
}

export function DatasetView() {
  const { products, refresh: refreshProducts } = useProducts();
  const [sets, setSets] = useState<ImageSet[]>([]);
  const [activeSetId, setActiveSetId] = useState<number | null>(null);
  const [items, setItems] = useState<ImageSetItem[]>([]);
  const [uploads, setUploads] = useState<UploadItem[]>([]);

  // New-dataset form — no product here; that's chosen per upload.
  const [creating, setCreating] = useState(false);
  /*
    A real customer, chosen from the list — not a typed name.

    It was free text, and the free text went into the composed name while
    customer_id stayed NULL. That is how "ACME DBB Laptop BRUSS-POC" ended up
    stored as a customer: it is a rig label somebody typed into a box asking for
    a customer, and nothing could tell the difference.
  */
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  useEffect(() => {
    api.listCustomers().then(setCustomers).catch(() => setCustomers([]));
  }, []);
  const customer = customers.find((c) => c.id === customerId)?.name ?? '';
  const [label, setLabel] = useState('');
  const [newSetName, setNewSetName] = useState('');
  const [search, setSearch] = useState('');

  // Product for the NEXT upload into this dataset.
  const [uploadProductId, setUploadProductId] = useState<number | undefined>();

  /**
   * Which images are picked, for saying "these are product A".
   *
   * A shoot is not one product: you photograph a morning's work, upload it, and
   * sort out what is what afterwards. Before this the only correction was
   * all-or-nothing — move every image in the dataset, or delete it and upload
   * again in batches — so two products in one upload had no answer at all.
   */
  /*
    Keyed on the item id as the API gives it: a string holding a number. Storing
    it as a number here and converting on the way in would mean two
    representations of one id in one component, and the comparison that then
    silently fails is `has()`, which returns false rather than complaining.
    Converted once, at the call.
  */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  /** Anchor for shift-click. Selecting 250 images one at a time is not a feature. */
  const [anchor, setAnchor] = useState<string | null>(null);
  /** Creating a product straight from what is selected, rather than in Settings. */
  const [newFromSelection, setNewFromSelection] = useState(false);
  const [newProduct, setNewProduct] = useState(false);
  const [drag, setDrag] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  /**
   * The upload product follows the dataset you are looking at.
   *
   * It used to keep whatever was chosen last, so switching datasets and
   * dragging images in filed them under the previous dataset's product without
   * a word. That is how 496 images ended up under the wrong one. A dataset
   * that already holds images now decides the default; only an empty one falls
   * back to the first product.
   */
  const activeSetForDefault = sets.find((s) => s.id === activeSetId) ?? null;
  useEffect(() => {
    const own = activeSetForDefault?.products ?? [];
    if (own.length > 0) {
      // The one it already holds most of — a mixed dataset still has a
      // dominant product, and that is the better guess than "whatever was
      // selected last".
      const dominant = [...own].sort((a, b) => b.count - a.count)[0]!;
      setUploadProductId(dominant.productId);
      return;
    }
    if (products.length > 0) setUploadProductId((cur) => cur ?? products[0]!.id);
  }, [activeSetId, activeSetForDefault, products]);

  const loadSets = useCallback(async () => {
    const s = await api.listImageSets(undefined, search || undefined);
    setSets(s);
    setActiveSetId((cur) => (cur && s.some((x) => x.id === cur) ? cur : (s[0]?.id ?? null)));
  }, [search]);

  useEffect(() => {
    void loadSets();
  }, [loadSets]);

  const loadItems = useCallback(async () => {
    if (!activeSetId) {
      setItems([]);
      return;
    }
    setItems(await api.imageSetItems(activeSetId));
  }, [activeSetId]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  // Cleared when the dataset changes: a selection carried across sets would act
  // on images the person is no longer looking at.
  useEffect(() => {
    setPicked(new Set());
    setAnchor(null);
  }, [activeSetId]);

  const suggestedName = composeSetName(customer, label);

  const createSet = async () => {
    const name = (newSetName || suggestedName).trim();
    if (!name) return;
    setError(null);
    try {
      if (customerId === null) {
        setError('Pick the customer this dataset belongs to.');
        return;
      }
      const created = await api.createImageSet({ customerId, name });
      setNewSetName('');
      setCustomerId(null);
      setLabel('');
      setCreating(false);
      await loadSets();
      setActiveSetId(created.id);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const addFiles = (files: FileList | File[]) => {
    const imgs = Array.from(files).filter((f) => f.type.startsWith('image/'));
    setUploads((prev) => [
      ...prev,
      ...imgs.map((file) => ({ file, preview: URL.createObjectURL(file), status: 'pending' as const })),
    ]);
  };

  const uploadAll = async () => {
    if (!uploadProductId || !activeSetId) return;
    setRunning(true);
    const queue = uploads.map((u, i) => ({ u, i })).filter(({ u }) => u.status !== 'done');

    const worker = async () => {
      for (;;) {
        const next = queue.shift();
        if (!next) return;
        const { i } = next;
        setUploads((prev) => prev.map((u, k) => (k === i ? { ...u, status: 'uploading' } : u)));
        try {
          await api.inspect(uploadProductId, uploads[i]!.file, uploads[i]!.file.name, activeSetId);
          setUploads((prev) => prev.map((u, k) => (k === i ? { ...u, status: 'done' } : u)));
        } catch (e) {
          setUploads((prev) =>
            prev.map((u, k) => (k === i ? { ...u, status: 'error', error: (e as Error).message } : u)),
          );
        }
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setRunning(false);
    await loadItems();
    await loadSets();
    setUploads((prev) => {
      prev.forEach((u) => URL.revokeObjectURL(u.preview));
      return [];
    });
  };

  /**
   * Apply one label to a whole dataset.
   *
   * Confirmed with the count, because this replaces existing labels rather
   * than adding to them — an image carries one current verdict, and stacking a
   * second would leave the exporter picking by timestamp.
   */
  const bulkLabel = async (label: 'GOOD' | 'BAD' | 'UNSURE' | null) => {
    if (!activeSetId) return;
    const n = items.length;
    const what = label === null ? `Clear the labels on ${n} images?` : `Mark all ${n} images as ${label}?`;
    if (!window.confirm(`${what}\n\nThis replaces any label they already have.`)) return;
    setRunning(true);
    try {
      const r = await api.labelImageSet(activeSetId, label);
      await loadItems();
      await loadSets();
      setError(null);
      window.alert(
        label === null ? `Cleared ${r.cleared} labels.` : `Labelled ${r.updated} images as ${label}.`,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const activeSet = sets.find((s) => s.id === activeSetId) ?? null;
  const pendingCount = uploads.filter((u) => u.status !== 'done').length;
  const uploadProductName = products.find((p) => p.id === uploadProductId)?.name ?? '';

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Datasets</h1>
        <div className="row">
          <input
            placeholder="Search customer or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button className="btn primary" onClick={() => setCreating((c) => !c)}>
            {creating ? '✕ Cancel' : '+ New dataset'}
          </button>
        </div>
      </div>

      {creating && (
        <div className="panel" style={{ marginBottom: 20 }}>
          <strong>New dataset</strong>
          <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Naming convention: <strong>Customer · Label · Date</strong>. You pick the product per
            upload inside the dataset — one dataset can hold several products.
          </p>
          <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
            <div style={{ minWidth: 240 }}>
              <label>Customer</label>
              <Combobox
                options={customers.map((c) => ({ value: String(c.id), label: c.name }))}
                value={customerId === null ? null : String(customerId)}
                onChange={(v) => setCustomerId(v ? Number(v) : null)}
                placeholder="Search customers…"
                emptyLabel="— pick a customer —"
                allowEmpty={false}
              />
            </div>
            <div>
              <label>Label (optional)</label>
              <input
                placeholder="e.g. batch-A"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Dataset name (auto-composed)</label>
              <input
                value={newSetName || suggestedName}
                onChange={(e) => setNewSetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void createSet()}
                style={{ width: '100%' }}
              />
            </div>
            <button className="btn primary" onClick={createSet}>
              Create
            </button>
          </div>
          {error && <div style={{ color: 'var(--bad)', marginTop: 10 }}>{error}</div>}
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '300px minmax(0, 1fr)' }}>
        {/* Dataset list */}
        <div className="panel" style={{ padding: 12, alignSelf: 'start' }}>
          <div
            className="muted"
            style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}
          >
            {sets.length} dataset{sets.length === 1 ? '' : 's'}
          </div>
          <div className="grid" style={{ gap: 6 }}>
            {sets.map((s) => (
              <button
                key={s.id}
                className={`chip ${activeSetId === s.id ? 'selected' : ''}`}
                style={{ textAlign: 'left', display: 'block', width: '100%' }}
                onClick={() => setActiveSetId(s.id)}
              >
                <div style={{ fontWeight: 700 }}>{s.name}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                  {s.annotatedCount}/{s.itemCount} annotated
                  {s.products.length > 0 && ` · ${s.products.map((p) => p.productName).join(', ')}`}
                </div>
              </button>
            ))}
            {sets.length === 0 && <span className="muted">No datasets yet.</span>}
          </div>
        </div>

        {/* Active dataset */}
        <div className="grid" style={{ alignContent: 'start' }}>
          {!activeSet ? (
            <div className="panel muted">Create a dataset to start uploading training images.</div>
          ) : (
            <>
              <div className="panel">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <strong style={{ fontSize: 18 }}>{activeSet.name}</strong>
                    <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                      {activeSet.itemCount} images · {activeSet.annotatedCount} annotated
                      {activeSet.products.length > 0 && (
                        <>
                          {' · '}
                          {activeSet.products.map((p) => `${p.productName} (${p.count})`).join(', ')}
                        </>
                      )}
                    </div>
                    {/*
                      Shown on every dataset, not only the unowned ones.

                      A dataset with no customer is invisible in that customer's
                      reporting while looking perfectly healthy here — there is
                      no symptom on this screen at all. Stating the owner, and
                      colouring its absence, is the only place the gap shows.
                    */}
                    <div className="row" style={{ gap: 8, marginTop: 8, alignItems: 'center' }}>
                      <span
                        className="muted"
                        style={{ fontSize: 12, color: activeSet.customerId ? undefined : 'var(--warn)' }}
                      >
                        {activeSet.customerId
                          ? `Customer: ${customers.find((c) => c.id === activeSet.customerId)?.name ?? activeSet.customerId}`
                          : 'No customer — this dataset appears in nobody’s reporting'}
                      </span>
                      <div style={{ minWidth: 210 }}>
                        <Combobox
                          options={customers.map((c) => ({ value: String(c.id), label: c.name }))}
                          value={activeSet.customerId === null ? null : String(activeSet.customerId)}
                          onChange={async (v) => {
                            if (!v) return;
                            setError(null);
                            try {
                              await api.setImageSetCustomer(activeSet.id, Number(v));
                              await loadSets();
                            } catch (e) {
                              setError((e as Error).message);
                            }
                          }}
                          placeholder="Search customers…"
                          emptyLabel="— assign a customer —"
                          allowEmpty={false}
                        />
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn good"
                    disabled={items.length === 0}
                    onClick={() =>
                      navigate('/annotate', {
                        state: { setId: activeSet.id, setName: activeSet.name },
                      })
                    }
                  >
                    Annotate this dataset →
                  </button>
                </div>

                {/* The product applies to the NEXT upload, not the whole dataset. */}
                <div className="row" style={{ marginTop: 16, alignItems: 'flex-end' }}>
                  <div>
                    <label>Upload these images as product</label>
                    <select
                      value={uploadProductId ?? ''}
                      onChange={(e) => {
                        if (e.target.value === '__new') {
                          setNewProduct(true);
                          return;
                        }
                        setUploadProductId(e.target.value ? Number(e.target.value) : undefined);
                      }}
                    >
                      {products.length === 0 && <option value="">No products yet</option>}
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                      {/* Creating the product belongs here, not in Settings.
                          Which product an image is only becomes clear while
                          you're looking at it, and leaving to invent a SKU
                          loses your place in the upload. */}
                      <option value="__new">+ New product…</option>
                    </select>
                  </div>
                  <span className="muted" style={{ fontSize: 12, paddingBottom: 10 }}>
                    Change this between uploads to mix products in one dataset.
                  </span>
                  {/* Correcting a whole dataset at once. Fixing a mis-filed
                      batch any other way means deleting and re-uploading it. */}
                  {items.length > 0 && uploadProductId && (
                    <button
                      className="btn sm"
                      style={{ marginBottom: 10 }}
                      title={`Move all ${items.length} images already in this dataset to ${uploadProductName}`}
                      onClick={async () => {
                        const wrong = items.filter((i) => i.productId !== uploadProductId).length;
                        if (wrong === 0) {
                          setError(`Every image here is already ${uploadProductName}.`);
                          return;
                        }
                        if (
                          !window.confirm(
                            `Move ${wrong} image${wrong === 1 ? '' : 's'} in this dataset to ` +
                              `${uploadProductName}?\n\nTheir annotations stay; only which ` +
                              `product they belong to changes.`,
                          )
                        ) {
                          return;
                        }
                        try {
                          const r = await api.setImageSetProduct(activeSet.id, uploadProductId);
                          setError(null);
                          await loadItems();
                          await loadSets();
                          window.alert(`Moved ${r.moved} images to ${uploadProductName}.`);
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                    >
                      ⇄ Move existing images here
                    </button>
                  )}
                </div>

                {/*
                  A new product inherits this dataset's customer, so nothing is
                  asked about it — you are already inside that customer. The
                  guard is for the datasets that predate the rule and still have
                  none; once the ownership migration runs, customerId is NOT NULL
                  and this branch becomes unreachable rather than wrong.
                */}
                {newProduct &&
                  (activeSet.customerId === null ? (
                    <div className="panel" style={{ marginTop: 12, color: 'var(--warn)' }}>
                      Give this dataset a customer first — a product has to belong to somebody.
                    </div>
                  ) : (
                    <NewProductInline
                      customerId={activeSet.customerId}
                      customerName={customers.find((c) => c.id === activeSet.customerId)?.name ?? null}
                      onCancel={() => setNewProduct(false)}
                      onCreated={async (p) => {
                        setNewProduct(false);
                        await refreshProducts();
                        setUploadProductId(p.id);
                      }}
                      onError={setError}
                    />
                  ))}
              </div>

              {/* A batch is often entirely good — reference samples — or
                  entirely defective. Clicking through six hundred images to say
                  the same thing six hundred times is data entry, not
                  annotation. */}
              {items.length > 0 && (
                <div
                  className="row"
                  style={{
                    gap: 10,
                    marginTop: 14,
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--panel-2)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ fontSize: 13 }}>
                    Label all <strong>{items.length}</strong> images in this dataset:
                  </span>
                  <button
                    className="btn sm good"
                    disabled={running}
                    onClick={() => void bulkLabel('GOOD')}
                  >
                    ✓ All GOOD
                  </button>
                  <button
                    className="btn sm"
                    disabled={running}
                    onClick={() => void bulkLabel('BAD')}
                  >
                    ■ All BAD
                  </button>
                  <button
                    className="btn sm"
                    disabled={running}
                    onClick={() => void bulkLabel('UNSURE')}
                  >
                    ? All UNSURE
                  </button>
                  <button className="btn sm" disabled={running} onClick={() => void bulkLabel(null)}>
                    ✕ Clear labels
                  </button>
                </div>
              )}

              <div
                className={`dropzone ${drag ? 'drag' : ''}`}
                style={{ padding: 36 }}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDrag(true);
                }}
                onDragLeave={() => setDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDrag(false);
                  addFiles(e.dataTransfer.files);
                }}
              >
                Drop images into <strong>{activeSet.name}</strong> as{' '}
                <strong>{uploadProductName}</strong>, or click to select
                <input
                  ref={inputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
              </div>

              {uploads.length > 0 && (
                <div className="panel">
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span>
                      {uploads.length} file{uploads.length > 1 ? 's' : ''} staged as{' '}
                      <strong>{uploadProductName}</strong>
                      {running && ' · uploading…'}
                    </span>
                    <div className="row">
                      <button className="btn sm" disabled={running} onClick={() => setUploads([])}>
                        Clear
                      </button>
                      <button
                        className="btn primary sm"
                        onClick={uploadAll}
                        disabled={running || pendingCount === 0 || !uploadProductId}
                      >
                        Upload {pendingCount}
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {uploads.map((u, i) => (
                      <img
                        key={i}
                        src={u.preview}
                        alt=""
                        title={u.error ?? u.file.name}
                        style={{
                          width: 64,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 6,
                          opacity: u.status === 'done' ? 0.35 : u.status === 'uploading' ? 0.6 : 1,
                          border:
                            u.status === 'error' ? '2px solid var(--bad)' : '2px solid transparent',
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="panel">
                {items.length === 0 ? (
                  <div className="muted">No images yet — upload some above.</div>
                ) : (
                  <>
                  {/*
                    Sticky, because the selecting happens while scrolling
                    through a few hundred thumbnails and the control that acts
                    on the selection must not be a scroll away. The chart
                    importer made exactly that mistake: the only button that
                    saved anything sat a thousand pixels above the work, and a
                    real chart was lost to it.
                  */}
                  <div
                    className="row"
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 4,
                      gap: 10,
                      padding: '8px 10px',
                      margin: '0 -10px 10px',
                      background: 'var(--bg-panel)',
                      borderBottom: '1px solid var(--border)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span style={{ fontSize: 13, color: picked.size ? 'var(--warn)' : 'var(--text-dim)' }}>
                      {picked.size
                        ? `${picked.size} selected`
                        : `${items.length} images — tick to select, shift-tick for a run`}
                    </span>
                    <button
                      className="btn sm"
                      onClick={() =>
                        setPicked((prev) =>
                          prev.size === items.length ? new Set() : new Set(items.map((i) => i.id)),
                        )
                      }
                    >
                      {picked.size === items.length && items.length > 0 ? 'Select none' : 'Select all'}
                    </button>
                    <div style={{ flex: 1 }} />
                    {picked.size > 0 && (
                      <>
                        <span className="muted" style={{ fontSize: 13 }}>These are</span>
                        {/*
                          The product you want often does not exist yet — the
                          selection IS the definition of it. Offering only
                          existing products means leaving the dataset, creating
                          one in Settings, coming back, and finding the
                          selection gone.
                        */}
                        <button
                          className="btn sm"
                          disabled={activeSet.customerId === null}
                          title={
                            activeSet.customerId === null
                              ? 'Give this dataset a customer first'
                              : `Create a product from the ${picked.size} selected image(s)`
                          }
                          onClick={() => setNewFromSelection(true)}
                        >
                          + New product
                        </button>
                        <div style={{ minWidth: 230 }}>
                          <Combobox
                            /*
                              Only this dataset's customer's products. The
                              constraint would refuse the others anyway; listing
                              them means somebody picks one and reads an error
                              where a working control belongs.
                            */
                            options={products
                              .filter((pr) => pr.customerId === activeSet.customerId)
                              .map((pr) => ({ value: String(pr.id), label: pr.name, hint: pr.sku }))}
                            value={null}
                            onChange={async (v) => {
                              if (!v) return;
                              setError(null);
                              try {
                                const r = await api.setImageSetItemsProduct(
                                  activeSet.id,
                                  [...picked].map(Number),
                                  Number(v),
                                );
                                setPicked(new Set());
                                setAnchor(null);
                                await loadItems();
                                await loadSets();
                                window.alert(
                                  `${r.moved} image(s) are now ${products.find((pr) => pr.id === Number(v))?.name}.`,
                                );
                              } catch (e) {
                                setError((e as Error).message);
                              }
                            }}
                            placeholder="Search products…"
                            emptyLabel="— pick a product —"
                            allowEmpty={false}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {newFromSelection && activeSet.customerId !== null && (
                    <NewProductInline
                      customerId={activeSet.customerId}
                      customerName={customers.find((c) => c.id === activeSet.customerId)?.name ?? null}
                      onCancel={() => setNewFromSelection(false)}
                      onCreated={async (p) => {
                        setNewFromSelection(false);
                        await refreshProducts();
                        /*
                          Created AND assigned in one go. Creating it and leaving
                          the selection unassigned would be the same trap as
                          before — the work looks done and nothing was written.
                        */
                        try {
                          const r = await api.setImageSetItemsProduct(
                            activeSet.id,
                            [...picked].map(Number),
                            p.id,
                          );
                          setPicked(new Set());
                          setAnchor(null);
                          await loadItems();
                          await loadSets();
                          window.alert(`${r.moved} image(s) are now ${p.name}.`);
                        } catch (e) {
                          setError((e as Error).message);
                        }
                      }}
                      onError={setError}
                    />
                  )}

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                      gap: 12,
                    }}
                  >
                    {items.map((it, idx) => (
                      <GalleryTile
                        key={it.id}
                        item={it}
                        picked={picked.has(it.id)}
                        onPick={(shift) => {
                          setPicked((prev) => {
                            const next = new Set(prev);
                            // Shift extends from the last one touched, which is
                            // the difference between sorting a shoot and
                            // clicking two hundred and fifty times.
                            if (shift && anchor !== null) {
                              const from = items.findIndex((x) => x.id === anchor);
                              if (from >= 0) {
                                const [a, b] = from < idx ? [from, idx] : [idx, from];
                                for (let k = a; k <= b; k += 1) next.add(items[k]!.id);
                                return next;
                              }
                            }
                            next.has(it.id) ? next.delete(it.id) : next.add(it.id);
                            return next;
                          });
                          setAnchor(it.id);
                        }}
                        onAnnotate={() =>
                          navigate('/annotate', {
                            state: {
                              imageKey: it.imageKey,
                              inspectionId: it.inspectionId,
                              productId: it.productId ?? undefined,
                              setId: activeSet.id,
                              setName: activeSet.name,
                              suggestedLabel: it.verdict ?? undefined,
                            },
                          })
                        }
                      />
                    ))}
                  </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GalleryTile({
  item,
  onAnnotate,
  picked,
  onPick,
}: {
  item: ImageSetItem;
  onAnnotate: () => void;
  picked: boolean;
  onPick: (shift: boolean) => void;
}) {
  const url = useImageUrl(item.imageKey);
  return (
    <div
      className="stat-tile"
      style={{
        padding: 8,
        position: 'relative',
        outline: picked ? '2px solid var(--accent)' : 'none',
      }}
    >
      {/*
        A tick in the corner rather than click-to-select on the tile: clicking
        the picture opens the annotator, which is the commoner action, and a
        mode toggle to swap the two is a thing to remember at exactly the moment
        somebody is concentrating on the images.
      */}
      <button
        aria-label={picked ? 'Deselect' : 'Select'}
        onClick={(e) => onPick(e.shiftKey)}
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          zIndex: 2,
          width: 20,
          height: 20,
          borderRadius: 4,
          border: '1px solid var(--border)',
          background: picked ? 'var(--accent)' : 'rgba(0,0,0,0.55)',
          color: '#fff',
          cursor: 'pointer',
          padding: 0,
          lineHeight: '18px',
          fontSize: 13,
        }}
      >
        {picked ? '✓' : ''}
      </button>
      <button
        onClick={onAnnotate}
        title={item.filename ?? item.imageKey}
        style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', width: '100%' }}
      >
        {url && (
          <img
            src={url}
            alt=""
            style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 6, display: 'block' }}
          />
        )}
      </button>
      {item.productName && (
        <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
          {item.productName}
        </div>
      )}
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 4, gap: 4 }}>
        {item.annotated ? (
          // The badge must wear the label's own colour. Hard-coding "good" put
          // a green tick on every defect, which is the one thing a QC tool
          // must never do.
          <span
            className={`badge ${LABEL_BADGE[item.annotatedLabel ?? 'UNSURE'] ?? 'unsure'}`}
            style={{ fontSize: 11 }}
          >
            {LABEL_ICON[item.annotatedLabel ?? 'UNSURE'] ?? '?'} {item.annotatedLabel}
          </span>
        ) : (
          <span className="muted" style={{ fontSize: 11 }}>
            unlabelled
          </span>
        )}
        {item.verdict && <MiniVerdict verdict={item.verdict} score={item.score} />}
      </div>
    </div>
  );
}

function MiniVerdict({ verdict, score }: { verdict: Verdict; score: number | null }) {
  return (
    <span style={{ fontSize: 11, fontFamily: 'var(--mono)' }} title="model prediction">
      <VerdictBadge verdict={verdict} />
      {score !== null && <span className="muted"> {score.toFixed(2)}</span>}
    </span>
  );
}
